import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { 
  CalendarDays, MapPin, Clock, CheckCircle2, XCircle, HelpCircle, 
  History, Search, Filter, RefreshCw, ChevronRight, Calendar,
  ThumbsUp, ThumbsDown, Hourglass, PartyPopper, FileText, Users,
  Phone, Mail, Utensils, ChevronDown, ChevronUp, Info, Navigation
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, isValid, eachDayOfInterval, addDays } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MesPropositionsPage() {
  const [propositions, setPropositions] = useState([]);
  const [stats, setStats] = useState({ total: 0, en_attente: 0, interesse: 0, confirme: 0, termine: 0, decline: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedCards, setExpandedCards] = useState({});
  
  // Dialog state for date selection
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [selectedProposition, setSelectedProposition] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");

  // Toggle card expansion
  const toggleCardExpansion = (propId) => {
    setExpandedCards(prev => ({ ...prev, [propId]: !prev[propId] }));
  };

  useEffect(() => {
    loadPropositions();
  }, []);

  const loadPropositions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/my-propositions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out declined propositions - they should be removed from view
      const allPropositions = response.data.propositions || [];
      const filteredPropositions = allPropositions.filter(p => p.display_status !== 'decline');
      
      setPropositions(filteredPropositions);
      
      // Adjust stats to exclude declined
      const originalStats = response.data.stats || { total: 0, en_attente: 0, interesse: 0, confirme: 0, termine: 0, decline: 0 };
      setStats({
        ...originalStats,
        total: originalStats.total - (originalStats.decline || 0),
        decline: 0  // Don't show decline count since we hide them
      });
    } catch (error) {
      console.error("Error loading propositions:", error);
      toast.error("Erreur lors du chargement des propositions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return dateStr;
      return format(date, "d MMM yyyy", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const formatDateRange = (debut, fin) => {
    const d1 = formatDate(debut);
    const d2 = formatDate(fin);
    if (d1 === d2 || !fin) return d1;
    return `${d1} - ${d2}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      en_attente: {
        label: "En attente",
        icon: Hourglass,
        className: "bg-yellow-100 text-yellow-800 border-yellow-300",
        description: "En attente de votre réponse"
      },
      interesse: {
        label: "Intéressé",
        icon: ThumbsUp,
        className: "bg-blue-100 text-blue-800 border-blue-300",
        description: "Vous avez marqué votre intérêt"
      },
      confirme: {
        label: "Confirmé",
        icon: CheckCircle2,
        className: "bg-green-100 text-green-800 border-green-300",
        description: "Vous êtes confirmé pour cet événement"
      },
      decline: {
        label: "Décliné",
        icon: XCircle,
        className: "bg-gray-100 text-gray-600 border-gray-300",
        description: "Vous avez décliné cette proposition"
      },
      termine: {
        label: "Terminé",
        icon: PartyPopper,
        className: "bg-purple-100 text-purple-800 border-purple-300",
        description: "Événement passé"
      }
    };
    return configs[status] || configs.en_attente;
  };

  const filteredPropositions = propositions.filter(p => {
    const matchesSearch = !searchTerm || 
      p.event_titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.event_lieu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.proposeur_nom?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = activeFilter === "all" || p.display_status === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Check if event is multi-date
  const isMultiDateEvent = (prop) => {
    if (!prop.event_date_debut || !prop.event_date_fin) return false;
    try {
      const start = parseISO(prop.event_date_debut);
      const end = parseISO(prop.event_date_fin);
      return start.getTime() !== end.getTime();
    } catch {
      return false;
    }
  };

  // Get available dates for multi-date events
  const getEventDates = (prop) => {
    if (!prop.event_date_debut || !prop.event_date_fin) return [];
    try {
      const start = parseISO(prop.event_date_debut);
      const end = parseISO(prop.event_date_fin);
      return eachDayOfInterval({ start, end });
    } catch {
      return [];
    }
  };

  // Handle response with date selection for multi-date events
  const handleInterestedClick = (prop) => {
    if (isMultiDateEvent(prop)) {
      setSelectedProposition(prop);
      setSelectedDate("");
      setShowDateDialog(true);
    } else {
      respondToProposition(prop.event_id, true);
    }
  };

  const respondToProposition = async (eventId, interested, dateSouhaitee = null) => {
    try {
      const token = localStorage.getItem("token");
      const payload = { interested };
      if (dateSouhaitee) {
        payload.date_souhaitee = dateSouhaitee;
      }
      await axios.post(`${API_URL}/api/events/${eventId}/participation-response`, 
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(interested ? "Intérêt confirmé !" : "Proposition déclinée");
      setShowDateDialog(false);
      loadPropositions();
    } catch (error) {
      console.error("Error responding:", error);
      toast.error("Erreur lors de la réponse");
    }
  };

  return (
    <div className="space-y-6" data-testid="mes-propositions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary" />
            Mes Propositions
          </h1>
          <p className="text-muted-foreground mt-1">
            Historique de toutes vos propositions d'événements
          </p>
        </div>
        <Button onClick={loadPropositions} variant="outline" className="gap-2" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards - 5 cards (no "Décliné" since they are removed) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-secondary">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'en_attente' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setActiveFilter('en_attente')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.en_attente}</div>
            <div className="text-xs text-muted-foreground">En attente</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'interesse' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setActiveFilter('interesse')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.interesse}</div>
            <div className="text-xs text-muted-foreground">Intéressé</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'confirme' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setActiveFilter('confirme')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.confirme}</div>
            <div className="text-xs text-muted-foreground">Confirmé</div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${activeFilter === 'termine' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setActiveFilter('termine')}
        >
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.termine}</div>
            <div className="text-xs text-muted-foreground">Terminé</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un événement, lieu, proposeur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Propositions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredPropositions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold text-secondary mb-2">
              {searchTerm || activeFilter !== 'all' ? "Aucun résultat" : "Aucune proposition"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm || activeFilter !== 'all' 
                ? "Essayez de modifier vos critères de recherche" 
                : "Vous n'avez pas encore reçu de propositions d'événements"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPropositions.map((prop) => {
            const statusConfig = getStatusConfig(prop.display_status);
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedCards[prop.id];
            const hasDetails = prop.horaire_debut || prop.couverts_estimes || prop.specialites_recherchees?.length > 0 || 
                              prop.info_complementaire || prop.contact_nom || prop.event_dates?.length > 0;
            
            return (
              <Card 
                key={prop.id} 
                className={`overflow-hidden transition-all hover:shadow-md ${
                  prop.display_status === 'en_attente' ? 'border-l-4 border-l-yellow-500' :
                  prop.display_status === 'interesse' ? 'border-l-4 border-l-blue-500' :
                  prop.display_status === 'confirme' ? 'border-l-4 border-l-green-500' :
                  prop.display_status === 'termine' ? 'border-l-4 border-l-purple-500' :
                  'border-l-4 border-l-gray-300'
                }`}
                data-testid={`proposition-card-${prop.event_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${statusConfig.className}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-secondary">
                              {prop.event_titre}
                            </h3>
                            {prop.is_planning_multiple && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                                Planning Multiple
                              </Badge>
                            )}
                            {prop.event_type && (
                              <Badge variant="outline" className="text-xs">
                                {prop.event_type === 'collectif' ? 'Collectif' : 'Privé'}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Infos essentielles */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4 text-primary" />
                              {prop.date_participation 
                                ? <span className="font-medium text-secondary">{formatDate(prop.date_participation)}</span>
                                : prop.date_souhaitee 
                                  ? <span>Souhaitée: {formatDate(prop.date_souhaitee)}</span>
                                  : formatDateRange(prop.event_date_debut, prop.event_date_fin)
                              }
                            </span>
                            
                            {(prop.horaire_debut || prop.horaire_fin) && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {prop.horaire_debut}{prop.horaire_fin && ` - ${prop.horaire_fin}`}
                              </span>
                            )}
                            
                            {prop.type_service && (
                              <Badge variant="secondary" className="text-xs">
                                {prop.type_service === 'midi' ? '🌞 Midi' : prop.type_service === 'soir' ? '🌙 Soir' : prop.type_service}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                            {prop.event_lieu && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {prop.event_lieu}
                              </span>
                            )}
                            {prop.event_adresse && prop.event_adresse !== prop.event_lieu && (
                              <span className="flex items-center gap-1">
                                <Navigation className="w-4 h-4" />
                                {prop.event_adresse}
                              </span>
                            )}
                          </div>
                          
                          {/* Couverts et nombre de FTs */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                            {prop.couverts_estimes && (
                              <span className="flex items-center gap-1">
                                <Utensils className="w-4 h-4" />
                                {prop.couverts_estimes} couverts estimés
                              </span>
                            )}
                            {prop.nombre_food_trucks_requis && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {prop.nombre_food_trucks_requis} food truck(s)
                              </span>
                            )}
                          </div>
                          
                          {prop.proposeur_nom && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Proposé par : {prop.proposeur_nom}
                            </p>
                          )}
                          
                          {/* Montant dû */}
                          {prop.is_participant && prop.montant_du > 0 && (
                            <div className="mt-2">
                              <Badge variant="outline" className={
                                prop.statut_paiement === 'paye' ? 'bg-green-50 text-green-700 border-green-300' :
                                prop.statut_paiement === 'exempte' ? 'bg-gray-50 text-gray-600 border-gray-300' :
                                'bg-orange-50 text-orange-700 border-orange-300'
                              }>
                                {prop.statut_paiement === 'paye' ? '✓ Payé' :
                                 prop.statut_paiement === 'exempte' ? 'Exempté' :
                                 `${prop.montant_du}€ à payer`}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={statusConfig.className}>
                        {statusConfig.label}
                      </Badge>
                      
                      {/* Action buttons for pending requests */}
                      {prop.display_status === 'en_attente' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 gap-1"
                            onClick={() => handleInterestedClick(prop)}
                          >
                            <ThumbsUp className="w-4 h-4" />
                            Intéressé
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-600 gap-1"
                            onClick={() => respondToProposition(prop.event_id, false)}
                          >
                            <ThumbsDown className="w-4 h-4" />
                            Décliner
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Bouton voir plus de détails */}
                  {hasDetails && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3 w-full text-muted-foreground"
                      onClick={() => toggleCardExpansion(prop.id)}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                      {isExpanded ? "Masquer les détails" : "Voir tous les détails"}
                    </Button>
                  )}
                  
                  {/* Détails complets (collapsible) */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                      {/* Spécialités recherchées */}
                      {prop.specialites_recherchees?.length > 0 && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-secondary mb-2 flex items-center gap-1">
                            <Utensils className="w-4 h-4 text-blue-600" /> Spécialités recherchées
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {prop.specialites_recherchees.map((spec, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Planning multiple - dates avec services */}
                      {prop.event_dates?.length > 0 && (
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-secondary mb-2 flex items-center gap-1">
                            <CalendarDays className="w-4 h-4 text-purple-600" /> Planning des services
                          </p>
                          <div className="space-y-2">
                            {prop.event_dates.map((dateInfo, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="font-medium">
                                  {dateInfo.date && format(parseISO(dateInfo.date), "EEEE d MMMM", { locale: fr })}
                                </span>
                                {dateInfo.services?.length > 0 && (
                                  <div className="ml-4 space-y-1 text-muted-foreground">
                                    {dateInfo.services.map((service, sIdx) => (
                                      <div key={sIdx}>
                                        {service.type === "midi" ? "🌞 Midi" : "🌙 Soir"} : {service.horaire_debut} - {service.horaire_fin}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Informations complémentaires */}
                      {prop.info_complementaire && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-secondary mb-1 flex items-center gap-1">
                            <Info className="w-4 h-4 text-gray-600" /> Informations complémentaires
                          </p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {prop.info_complementaire}
                          </p>
                        </div>
                      )}
                      
                      {/* Contact organisateur */}
                      {(prop.contact_nom || prop.contact_telephone || prop.contact_email) && (
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-secondary mb-2 flex items-center gap-1">
                            <Users className="w-4 h-4 text-green-600" /> Contact organisateur
                          </p>
                          <div className="space-y-1 text-sm">
                            {prop.contact_nom && (
                              <p className="text-muted-foreground">{prop.contact_nom}</p>
                            )}
                            {prop.contact_telephone && (
                              <a href={`tel:${prop.contact_telephone}`} className="flex items-center gap-1 text-primary hover:underline">
                                <Phone className="w-4 h-4" /> {prop.contact_telephone}
                              </a>
                            )}
                            {prop.contact_email && (
                              <a href={`mailto:${prop.contact_email}`} className="flex items-center gap-1 text-primary hover:underline">
                                <Mail className="w-4 h-4" /> {prop.contact_email}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Timestamps */}
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {prop.created_at && (
                      <span>Reçu le {formatDate(prop.created_at)}</span>
                    )}
                    {prop.responded_at && (
                      <span>• Répondu le {formatDate(prop.responded_at)}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog for date selection on multi-date events */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Choisir votre date de participation
            </DialogTitle>
          </DialogHeader>
          
          {selectedProposition && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium text-secondary">{selectedProposition.event_titre}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatDateRange(selectedProposition.event_date_debut, selectedProposition.event_date_fin)}
                </p>
                {selectedProposition.event_lieu && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {selectedProposition.event_lieu}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Date souhaitée *</Label>
                <Select value={selectedDate} onValueChange={setSelectedDate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une date" />
                  </SelectTrigger>
                  <SelectContent>
                    {getEventDates(selectedProposition).map((date, idx) => (
                      <SelectItem key={idx} value={format(date, "yyyy-MM-dd")}>
                        {format(date, "EEEE d MMMM yyyy", { locale: fr })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Cet événement a lieu sur plusieurs jours. Veuillez sélectionner la date où vous souhaitez participer.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDateDialog(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              disabled={!selectedDate}
              onClick={() => {
                if (selectedProposition && selectedDate) {
                  respondToProposition(selectedProposition.event_id, true, selectedDate);
                }
              }}
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Confirmer mon intérêt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
